import React from "react"
import toast from "react-hot-toast"
import { Html5Qrcode } from "html5-qrcode"
import { useApp } from "../context/AppContext"
import { Icon, useT } from "../shared"
import { useIsMobile } from "../shared/useIsMobile"
import LoginShell from "./LoginShell"
import { requestPasswordReset, resetPassword } from "../api/auth.api"

/* ============================================================
   Login Screen — PRD §FR-1: QR Login (primary) + Email/Password
   Visual: cinematic maritime split (osct-2 design system)
   ============================================================ */

const DEMO_USERS = import.meta.env.DEV ? {
  participant: { email: "peserta@osct.id", password: "password123" },
  trainer: { email: "trainer@osct.id", password: "password123" },
  admin: { email: "admin@osct.id", password: "password123" },
} : {}

// Kode kelas selalu 6 karakter dari alfabet tetap ini (lihat buatKodeKelas()
// di gladhy-backend/src/routes/programs.js) — dipakai untuk menebak bentuk
// isian di langkah identify tanpa perlu menebak dari kata sandi.
const CLASS_CODE_RE = /^[ACDEFGHJKLMNPQRTUVWXY34679]{6}$/i


/* ── Animated ocean background ─────────────────────────────── */
function OceanScene() {
  return (
    <>
      {/* Radial sun glow */}
      <div style={{
        position: "absolute", top: "-10%", right: "-15%",
        width: "60%", height: "60%",
        background: "radial-gradient(circle, rgba(238,155,0,0.18) 0%, transparent 60%)",
        pointerEvents: "none",
      }} />

      {/* Sun disk */}
      <div style={{
        position: "absolute", top: "18%", right: "14%",
        width: 100, height: 100, borderRadius: "50%",
        background: "radial-gradient(circle, #FFD180 0%, #EE9B00 70%, transparent 100%)",
        opacity: 0.6, filter: "blur(2px)",
        pointerEvents: "none",
      }} />

      {/* Layered animated waves */}
      <svg viewBox="0 0 800 600" preserveAspectRatio="xMidYMax slice" style={{
        position: "absolute", inset: 0,
        width: "100%", height: "100%", opacity: 0.9,
        pointerEvents: "none",
      }}>
        <defs>
          <linearGradient id="wv-1" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="#0A9396" stopOpacity="0.15" />
            <stop offset="1" stopColor="#0A9396" stopOpacity="0.4" />
          </linearGradient>
          <linearGradient id="wv-2" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="#005f73" stopOpacity="0.5" />
            <stop offset="1" stopColor="#003a47" stopOpacity="0.9" />
          </linearGradient>
          <linearGradient id="wv-3" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="#02151B" />
            <stop offset="1" stopColor="#000" />
          </linearGradient>
        </defs>

        {/* Sun reflection on water */}
        <g opacity="0.5">
          {Array.from({ length: 8 }).map((_, i) => (
            <ellipse key={i} cx="640" cy={420 + i * 18} rx={20 + i * 8} ry="3"
              fill="#EE9B00" opacity={(0.6 - i * 0.07).toFixed(2)} />
          ))}
        </g>

        {/* Wave layer 1 — far */}
        <path d="M0 440 Q 100 410, 200 430 T 400 425 T 600 430 T 800 415 L 800 600 L 0 600 Z" fill="url(#wv-1)">
          <animate attributeName="d" dur="9s" repeatCount="indefinite" values="
            M0 440 Q 100 410, 200 430 T 400 425 T 600 430 T 800 415 L 800 600 L 0 600 Z;
            M0 435 Q 100 420, 200 422 T 400 432 T 600 422 T 800 425 L 800 600 L 0 600 Z;
            M0 440 Q 100 410, 200 430 T 400 425 T 600 430 T 800 415 L 800 600 L 0 600 Z" />
        </path>

        {/* Wave layer 2 — mid */}
        <path d="M0 480 Q 150 450, 300 475 T 600 470 T 900 480 L 800 600 L 0 600 Z" fill="url(#wv-2)">
          <animate attributeName="d" dur="7s" repeatCount="indefinite" values="
            M0 480 Q 150 450, 300 475 T 600 470 T 900 480 L 800 600 L 0 600 Z;
            M0 475 Q 150 470, 300 462 T 600 478 T 900 470 L 800 600 L 0 600 Z;
            M0 480 Q 150 450, 300 475 T 600 470 T 900 480 L 800 600 L 0 600 Z" />
        </path>

        {/* Wave layer 3 — near */}
        <path d="M0 530 Q 100 510, 200 525 T 400 520 T 600 528 T 800 515 L 800 600 L 0 600 Z" fill="url(#wv-3)">
          <animate attributeName="d" dur="5s" repeatCount="indefinite" values="
            M0 530 Q 100 510, 200 525 T 400 520 T 600 528 T 800 515 L 800 600 L 0 600 Z;
            M0 528 Q 100 520, 200 515 T 400 530 T 600 518 T 800 525 L 800 600 L 0 600 Z;
            M0 530 Q 100 510, 200 525 T 400 520 T 600 528 T 800 515 L 800 600 L 0 600 Z" />
        </path>

        {/* Distant ship silhouette */}
        <g transform="translate(180,432)" opacity="0.6">
          <path d="M0 0 L 50 0 L 46 6 L 4 6 Z" fill="#000" />
          <rect x="14" y="-10" width="22" height="10" fill="#000" />
          <rect x="22" y="-22" width="6" height="14" fill="#000" />
        </g>

        {/* Containment boom — signature OPRC element */}
        <g transform="translate(0,470)" opacity="0.85">
          <path d="M 60 20 Q 220 60, 400 20 Q 580 -20, 720 22"
            fill="none" stroke="#EE9B00" strokeWidth="2.5" strokeLinecap="round">
            <animate attributeName="stroke-dashoffset" from="0" to="24" dur="3s" repeatCount="indefinite" />
          </path>
          {[100, 180, 260, 340, 420, 500, 580, 660].map((x, i) => (
            <circle key={i} cx={x} cy={20 + Math.sin(i * 0.8) * 12} r="3" fill="#EE9B00" />
          ))}
        </g>
      </svg>

      {/* Twinkling stars */}
      {Array.from({ length: 18 }).map((_, i) => {
        const x = 5 + (i * 53) % 90
        const y = 5 + ((i * 17) % 30)
        const size = i % 3 === 0 ? 1.5 : 1
        return (
          <div key={i} style={{
            position: "absolute",
            left: `${x}%`, top: `${y}%`,
            width: size, height: size,
            background: "white", borderRadius: "50%",
            opacity: 0.4 + (i % 5) * 0.1,
            boxShadow: "0 0 4px rgba(255,255,255,0.6)",
            pointerEvents: "none",
          }} />
        )
      })}
    </>
  )
}

/* ── Loading spinner ────────────────────────────────────────── */
function Spinner() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      style={{ animation: "osct-spin 0.8s linear infinite", flexShrink: 0 }}>
      <style>{`@keyframes osct-spin { to { transform: rotate(360deg); } }`}</style>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" opacity="0.25" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  )
}

/* ── Main login screen ──────────────────────────────────────── */
function LoginScreen() {
  const t = useT()
  const { lang, setLang, login, completeMfaLogin, loginWithQR, joinWithCode, navigate, branding } = useApp()

  // Nama lembaga untuk teks di dalam formulir; tampilan lainnya ada di LoginShell.
  const orgName = branding?.institution_name || 'Gladhy'
  const isMobile = useIsMobile()

  // Alur login satu pintu: 'identify' (satu kolom: email atau kode kelas) →
  // 'password' | 'join' tergantung bentuk isian. 'qr' dijangkau lewat tautan
  // terpisah dari langkah identify, bukan tab yang sejajar.
  const [step, setStep] = React.useState("identify")
  const [identifier, setIdentifier] = React.useState("")
  const [identifyError, setIdentifyError] = React.useState(null)

  // Gabung kelas — peserta yang belum pernah ada di sistem mendaftarkan diri
  // dengan kode yang ditampilkan pelatih.
  const [joinCode, setJoinCode] = React.useState("")
  const [joinName, setJoinName] = React.useState("")
  const [joinCompany, setJoinCompany] = React.useState("")
  const [joinPlaceOfBirth, setJoinPlaceOfBirth] = React.useState("")
  const [joinDateOfBirth, setJoinDateOfBirth] = React.useState("")
  const [joinEmail, setJoinEmail] = React.useState("")
  const [joinPhone, setJoinPhone] = React.useState("")
  // Opsional — kalau diisi, peserta bisa masuk lewat langkah "password" nanti
  // alih-alih harus mengetik kode kelas setiap kali.
  const [joinPassword, setJoinPassword] = React.useState("")
  // Langkah konfirmasi sebelum masuk. Nama yang salah ketik menjadi orang yang
  // berbeda di mata sistem — nilai pretest dan posttest-nya terpisah, dan tidak
  // ada galat apa pun yang memberi tahu. Satu layar ini yang menangkapnya,
  // selagi masih bisa diperbaiki.
  const [joinConfirm, setJoinConfirm] = React.useState(false)

  // Email/password form state — pre-filled only in dev for convenience
  const [email, setEmail] = React.useState(import.meta.env.DEV ? "peserta@osct.id" : "")
  const [pw, setPw] = React.useState(import.meta.env.DEV ? "password123" : "")
  const [loading, setLoading] = React.useState(false)
  // 2FA: setelah kata sandi benar, akun ber-2FA meminta kode. `mfaToken` hanya
  // bisa ditukar di langkah ini dan berumur 5 menit.
  const [mfaToken, setMfaToken] = React.useState("")
  const [mfaCode, setMfaCode] = React.useState("")
  const [mfaRecovery, setMfaRecovery] = React.useState(false)

  // QR camera state
  const [qrTokenInput, setQrTokenInput] = React.useState("")
  const [scanning, setScanning] = React.useState(false)
  const [cameras, setCameras] = React.useState([])
  const [selectedCamId, setSelectedCamId] = React.useState(null)
  const [cameraError, setCameraError] = React.useState(null)
  const html5QrRef = React.useRef(null)
  const lastTokenRef = React.useRef(null)

  // Password reset flow: 'login' | 'forgot' | 'reset' | 'reset-done'
  const [authView, setAuthView] = React.useState("login")
  const [resetToken, setResetToken] = React.useState("")
  const [resetEmail, setResetEmail] = React.useState("")
  const [newPassword, setNewPassword] = React.useState("")
  const [confirmPassword, setConfirmPassword] = React.useState("")

  // Auto-detect reset token from URL on mount
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const tok = params.get("reset")
    if (tok) { setResetToken(tok); setAuthView("reset") }
  }, [])

  // ── Camera detection when QR step is active ──
  React.useEffect(() => {
    if (step !== "qr") return
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError(lang === "id"
        ? "Browser tidak mendukung akses kamera. Pakai Chrome/Safari modern."
        : "Browser does not support camera. Use modern Chrome/Safari.")
      return
    }
    Html5Qrcode.getCameras()
      .then((devices) => {
        setCameras(devices || [])
        if (devices && devices.length > 0) {
          const back = devices.find((d) => /back|rear|environment/i.test(d.label))
          setSelectedCamId(back?.id || devices[devices.length - 1].id)
          setCameraError(null)
        } else {
          setCameraError(lang === "id" ? "Tidak ada kamera terdeteksi." : "No camera detected.")
        }
      })
      .catch((e) => {
        setCameraError((lang === "id" ? "Gagal mendeteksi kamera: " : "Failed to detect camera: ") + (e?.message || e))
      })
  }, [step, lang])

  // ── Camera lifecycle ──
  React.useEffect(() => {
    if (!scanning || !selectedCamId) return
    let cancelled = false
    const html5 = new Html5Qrcode("qr-reader-login")
    html5QrRef.current = html5
    html5
      .start(selectedCamId, { fps: 10, qrbox: { width: 240, height: 240 } },
        (decodedText) => handleScannedToken(decodedText),
        () => { })
      .catch((e) => {
        if (cancelled) return
        setCameraError((lang === "id" ? "Gagal mulai kamera: " : "Failed to start camera: ") + (e?.message || String(e)))
        setScanning(false)
      })
    return () => {
      cancelled = true
      try { html5.stop().catch(() => {}) } catch {}
      try { html5.clear() } catch {}
      html5QrRef.current = null
    }
  }, [scanning, selectedCamId, lang]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handlers ──
  const handleScannedToken = React.useCallback(async (raw) => {
    let token = raw
    try { const p = JSON.parse(raw); if (p?.t) token = p.t } catch { }
    if (lastTokenRef.current === token) return
    lastTokenRef.current = token
    setScanning(false)
    await handleQRLogin(token)
    setTimeout(() => { lastTokenRef.current = null }, 2000)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Langkah 1: satu kolom, dua kemungkinan bentuk. Deteksi berdasarkan
  // bentuk isian, bukan tebak-tebakan — kalau tidak cocok keduanya, minta
  // diperbaiki daripada menebak.
  function handleIdentifyContinue(e) {
    e?.preventDefault?.()
    const v = identifier.trim()
    if (!v) return
    if (v.includes("@")) {
      setEmail(v)
      setIdentifyError(null)
      setStep("password")
      return
    }
    if (CLASS_CODE_RE.test(v)) {
      setJoinCode(v.toUpperCase())
      setIdentifyError(null)
      setStep("join")
      return
    }
    setIdentifyError(lang === "id"
      ? "Masukkan email atau kode kelas 6 karakter"
      : "Enter an email or a 6-character class code")
  }

  function backToIdentify() {
    setStep("identify")
    setIdentifyError(null)
  }

  async function handleEmailLogin(e) {
    e?.preventDefault?.()
    setLoading(true)
    try {
      const data = await login(email, pw)
      if (data?.mfaRequired) {
        setMfaToken(data.mfaToken)
        setMfaCode("")
        setMfaRecovery(false)
        setStep("mfa")
        return
      }
      toast.success(lang === "id" ? "Berhasil masuk" : "Signed in")
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message)
    } finally { setLoading(false) }
  }

  async function handleMfaSubmit(e) {
    e?.preventDefault?.()
    setLoading(true)
    try {
      await completeMfaLogin(mfaToken, mfaCode.trim())
      setPw("")
      toast.success(lang === "id" ? "Berhasil masuk" : "Signed in")
    } catch (err) {
      const code = err?.response?.data?.code
      toast.error(err?.response?.data?.error || err.message)
      // Sesi verifikasi (5 menit) habis: ulangi dari kata sandi.
      if (code === "MFA_TOKEN_EXPIRED") { setStep("password"); setMfaToken("") }
      else setMfaCode("")
    } finally { setLoading(false) }
  }

  async function handleJoin() {
    setLoading(true)
    try {
      await joinWithCode({
        code: joinCode.trim(),
        fullName: joinName.trim(),
        company: joinCompany.trim(),
        placeOfBirth: joinPlaceOfBirth.trim(),
        dateOfBirth: joinDateOfBirth,
        email: joinEmail.trim() || undefined,
        phone: joinPhone.trim() || undefined,
        password: joinPassword.trim() || undefined,
      })
      toast.success(lang === "id" ? "Selamat datang" : "Welcome")
    } catch (err) {
      // Kembali ke formulir supaya peserta bisa memperbaiki, bukan terjebak di
      // layar konfirmasi yang tidak bisa diapa-apakan.
      setJoinConfirm(false)
      toast.error(err?.response?.data?.error || err.message)
    } finally { setLoading(false) }
  }

  async function handleQRLogin(token) {
    setLoading(true)
    try {
      await loginWithQR(token)
      toast.success(lang === "id" ? "Berhasil masuk via QR" : "QR sign-in successful")
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message)
    } finally { setLoading(false) }
  }

  async function handleDemoLogin(roleKey) {
    const creds = DEMO_USERS[roleKey]
    if (!creds) return
    setLoading(true)
    try {
      await login(creds.email, creds.password)
      toast.success(lang === "id" ? `Masuk sebagai ${roleKey}` : `Signed in as ${roleKey}`)
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message)
    } finally { setLoading(false) }
  }

  async function handleForgotPassword(e) {
    e?.preventDefault?.()
    if (!resetEmail) return
    setLoading(true)
    try {
      await requestPasswordReset(resetEmail)
      toast.success(lang === "id"
        ? "Jika email terdaftar, link reset sudah dikirim."
        : "If the email is registered, a reset link has been sent.")
      setAuthView("login"); setResetEmail("")
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message)
    } finally { setLoading(false) }
  }

  async function handleResetPassword(e) {
    e?.preventDefault?.()
    if (newPassword.length < 10) { toast.error(lang === "id" ? "Password minimal 10 karakter" : "Password must be at least 10 characters"); return }
    if (newPassword !== confirmPassword) { toast.error(lang === "id" ? "Konfirmasi password tidak cocok" : "Passwords do not match"); return }
    setLoading(true)
    try {
      await resetPassword({ token: resetToken, password: newPassword })
      toast.success(lang === "id" ? "Password berhasil di-reset. Silakan login." : "Password reset. Please sign in.")
      const url = new URL(window.location.href)
      url.searchParams.delete("reset")
      window.history.replaceState({}, "", url.toString())
      setAuthView("login"); setNewPassword(""); setConfirmPassword(""); setResetToken("")
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message)
    } finally { setLoading(false) }
  }

  // ── Render ──
  return (
    <LoginShell branding={branding} lang={lang} setLang={setLang} isMobile={isMobile}>
          {/* ── Forgot password view ── */}
          {authView === "forgot" && (
            <form className="col gap-4" onSubmit={handleForgotPassword}>
              <div>
                <div className="eyebrow">{lang === "id" ? "Reset password" : "Reset password"}</div>
                <h2 style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.02em", marginTop: 6 }}>
                  {lang === "id" ? "Lupa kata sandi?" : "Forgot your password?"}
                </h2>
                <p className="muted mt-2" style={{ fontSize: 14 }}>
                  {lang === "id"
                    ? "Masukkan email akun Anda — kami kirim link reset."
                    : "Enter your account email and we'll send a reset link."}
                </p>
              </div>
              <div className="field">
                <label className="field-label">{t.email || "Email"}</label>
                <input className="input" type="email" value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)} required autoComplete="username" />
              </div>
              <button className="btn-primary btn-lg btn-block" type="submit" disabled={loading || !resetEmail}>
                {loading ? <><Spinner /> {lang === "id" ? "Mengirim…" : "Sending…"}</> : (lang === "id" ? "Kirim link reset" : "Send reset link")}
              </button>
              <button type="button" className="btn-ghost btn-sm btn-block" onClick={() => setAuthView("login")}>
                ← {lang === "id" ? "Kembali ke login" : "Back to sign in"}
              </button>
            </form>
          )}

          {/* ── Reset password view ── */}
          {authView === "reset" && (
            <form className="col gap-4" onSubmit={handleResetPassword}>
              <div>
                <div className="eyebrow">{lang === "id" ? "Password baru" : "New password"}</div>
                <h2 style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.02em", marginTop: 6 }}>
                  {lang === "id" ? "Buat password baru" : "Set new password"}
                </h2>
                <p className="muted mt-2" style={{ fontSize: 14 }}>
                  {lang === "id" ? "Minimal 10 karakter, dan bukan kata sandi yang umum dipakai." : "At least 10 characters, and not a commonly used password."}
                </p>
              </div>
              <div className="field">
                <label className="field-label">{lang === "id" ? "Password baru" : "New password"}</label>
                <input className="input" type="password" value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)} required minLength={10} autoComplete="new-password" />
              </div>
              <div className="field">
                <label className="field-label">{lang === "id" ? "Konfirmasi password" : "Confirm password"}</label>
                <input className="input" type="password" value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)} required minLength={10} autoComplete="new-password" />
              </div>
              <button className="btn-primary btn-lg btn-block" type="submit" disabled={loading || !newPassword || !confirmPassword}>
                {loading ? <><Spinner /> {lang === "id" ? "Memproses…" : "Processing…"}</> : (lang === "id" ? "Simpan password" : "Save password")}
              </button>
              <button type="button" className="btn-ghost btn-sm btn-block"
                onClick={() => {
                  const url = new URL(window.location.href)
                  url.searchParams.delete("reset")
                  window.history.replaceState({}, "", url.toString())
                  setAuthView("login"); setResetToken("")
                }}>
                {lang === "id" ? "Batal" : "Cancel"}
              </button>
            </form>
          )}

          {/* ── Main login view ── */}
          {authView === "login" && (
            <>
              <div className="eyebrow">Sign in</div>
              <h2 style={{ fontSize: 30, fontWeight: 600, letterSpacing: "-0.02em", marginTop: 6 }}>
                {t.loginTitle || "Welcome back"}
              </h2>
              <p className="muted mt-2" style={{ fontSize: 14 }}>
                {branding?.tagline || t.loginSubtitle}
              </p>

              {/* ─ Langkah 1: satu kolom — email atau kode kelas ─ */}
              {step === "identify" && (
                <form onSubmit={handleIdentifyContinue} style={{ marginTop: 28 }} className="col gap-3">
                  <div className="field">
                    <label className="field-label">{lang === "id" ? "Email atau kode kelas" : "Email or class code"}</label>
                    <input className="input" value={identifier}
                      onChange={(e) => { setIdentifier(e.target.value); setIdentifyError(null) }}
                      placeholder={lang === "id" ? "email@perusahaan.com atau X7QAT4" : "email@company.com or X7QAT4"}
                      autoComplete="username" autoCapitalize="none" />
                    {identifyError && (
                      <div className="tiny mt-2" style={{ color: "var(--danger)" }}>{identifyError}</div>
                    )}
                  </div>
                  <button className="btn-primary btn-lg btn-block" type="submit" disabled={!identifier.trim()}>
                    {lang === "id" ? "Lanjut" : "Continue"} →
                  </button>
                  <button type="button" className="btn btn-secondary btn-block"
                    onClick={() => { setScanning(false); setStep("qr") }}>
                    <Icon name="qr" size={14} /> {lang === "id" ? "Scan QR" : "Scan QR"}
                  </button>

                  {/* Demo login — dev only, hidden in production build */}
                  {import.meta.env.DEV && <DemoCard lang={lang} loading={loading} onLogin={handleDemoLogin} />}
                </form>
              )}

              {/* ─ Langkah 2: email terdeteksi → password ─ */}
              {step === "password" && (
                <form onSubmit={handleEmailLogin} style={{ marginTop: 24 }} className="col gap-3">
                  <button type="button" onClick={backToIdentify}
                    style={{ fontSize: 12, color: "var(--text-3)", background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left" }}>
                    ← {lang === "id" ? "ganti" : "change"} · {email}
                  </button>
                  <div className="field">
                    <div className="row between">
                      <label className="field-label">{t.password || "Password"}</label>
                      <button type="button"
                        onClick={() => { setResetEmail(email); setAuthView("forgot") }}
                        style={{ fontSize: 11, color: "var(--program-color-deep)", fontWeight: 500, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                        {t.forgotPassword || "Forgot?"}
                      </button>
                    </div>
                    <div style={{ position: "relative" }}>
                      <input className="input" type="password" value={pw}
                        onChange={(e) => setPw(e.target.value)}
                        autoComplete="current-password" style={{ paddingLeft: 36 }} autoFocus />
                      <Icon name="lock" size={14} style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: "var(--text-3)" }} />
                    </div>
                  </div>

                  <button className="btn-primary btn-lg btn-block" type="submit" disabled={loading}
                    style={{ marginTop: 4 }}>
                    {loading ? <><Spinner /> {lang === "id" ? "Memproses…" : "Signing in…"}</> : <>{t.signIn || "Sign in"} →</>}
                  </button>
                </form>
              )}

              {/* ─ Langkah 3 (akun ber-2FA): kode dari aplikasi autentikator ─ */}
              {step === "mfa" && (
                <form onSubmit={handleMfaSubmit} style={{ marginTop: 24 }} className="col gap-3">
                  <button type="button" onClick={() => { setStep("password"); setMfaToken(""); setMfaCode("") }}
                    style={{ fontSize: 12, color: "var(--text-3)", background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left" }}>
                    ← {lang === "id" ? "kembali" : "back"} · {email}
                  </button>
                  <div className="field">
                    <label className="field-label">
                      {mfaRecovery
                        ? (lang === "id" ? "Kode pemulihan" : "Recovery code")
                        : (lang === "id" ? "Kode verifikasi 6 digit" : "6-digit verification code")}
                    </label>
                    <input className="input" value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value)}
                      inputMode={mfaRecovery ? "text" : "numeric"}
                      autoComplete="one-time-code"
                      maxLength={mfaRecovery ? 16 : 7}
                      placeholder={mfaRecovery ? "XXXXX-XXXXX" : "123456"}
                      style={{ letterSpacing: mfaRecovery ? 1 : 4, fontFamily: "var(--font-mono)" }}
                      autoFocus />
                    <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                      {mfaRecovery
                        ? (lang === "id" ? "Setiap kode pemulihan hanya berlaku sekali." : "Each recovery code works only once.")
                        : (lang === "id" ? "Buka aplikasi autentikator Anda dan masukkan kode yang tampil." : "Open your authenticator app and enter the code shown.")}
                    </div>
                  </div>
                  <button className="btn-primary btn-lg btn-block" type="submit"
                    disabled={loading || mfaCode.trim().length < 6} style={{ marginTop: 4 }}>
                    {loading ? <><Spinner /> {lang === "id" ? "Memverifikasi…" : "Verifying…"}</> : <>{lang === "id" ? "Verifikasi" : "Verify"} →</>}
                  </button>
                  <button type="button" onClick={() => { setMfaRecovery((v) => !v); setMfaCode("") }}
                    style={{ fontSize: 12, color: "var(--program-color-deep)", fontWeight: 500, background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left" }}>
                    {mfaRecovery
                      ? (lang === "id" ? "Pakai kode dari aplikasi autentikator" : "Use the authenticator app code")
                      : (lang === "id" ? "Ponsel hilang? Pakai kode pemulihan" : "Lost your phone? Use a recovery code")}
                  </button>
                </form>
              )}

              {/* ─ Gabung kelas ─ */}
              {step === "join" && (
                joinConfirm ? (
                  <div style={{ marginTop: 24 }} className="col gap-3">
                    <div style={{
                      padding: "20px 18px",
                      background: "var(--surface-2)",
                      border: "1px solid var(--border-subtle)",
                      borderRadius: "var(--r-lg)",
                    }}>
                      <div style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 10 }}>
                        {lang === "id" ? "Anda akan masuk sebagai" : "You will sign in as"}
                      </div>
                      <div style={{ fontSize: 20, fontWeight: 600, lineHeight: 1.25 }}>{joinName.trim()}</div>
                      {joinCompany.trim() && (
                        <div style={{ fontSize: 15, color: "var(--text-2)", marginTop: 4 }}>{joinCompany.trim()}</div>
                      )}
                      <div style={{ fontSize: 13, color: "var(--text-2)", marginTop: 8, lineHeight: 1.5 }}>
                        {joinPlaceOfBirth.trim()}{joinPlaceOfBirth.trim() && joinDateOfBirth ? ", " : ""}
                        {joinDateOfBirth}
                      </div>
                      {joinEmail.trim() && (
                        <div style={{ fontSize: 13, color: "var(--text-2)", marginTop: 2 }}>{joinEmail.trim()}</div>
                      )}
                      {joinPhone.trim() && (
                        <div style={{ fontSize: 13, color: "var(--text-2)", marginTop: 2 }}>{joinPhone.trim()}</div>
                      )}
                      {joinPassword && (
                        <div className="row gap-1" style={{ fontSize: 12.5, color: "var(--text-2)", marginTop: 8, alignItems: "center" }}>
                          <Icon name="lock" size={11} />
                          {lang === "id" ? "Kata sandi disetel" : "Password will be set"}
                        </div>
                      )}
                      <div style={{ fontSize: 12.5, color: "var(--text-3)", marginTop: 14, lineHeight: 1.55 }}>
                        {lang === "id"
                          ? "Periksa ejaannya. Data ini dipakai untuk mencatat nilai pretest, posttest, kehadiran, dan sertifikat Anda."
                          : "Check the spelling. This data records your pretest, posttest, attendance and certificate."}
                      </div>
                    </div>
                    <button className="btn-primary btn-lg btn-block" type="button" disabled={loading}
                      onClick={handleJoin}>
                      {loading
                        ? <><Spinner /> {lang === "id" ? "Memproses…" : "Signing in…"}</>
                        : <>{lang === "id" ? "Ya, benar — masuk" : "Yes, that's me — sign in"} →</>}
                    </button>
                    <button type="button" className="btn btn-secondary btn-block" disabled={loading}
                      onClick={() => setJoinConfirm(false)}>
                      {lang === "id" ? "Perbaiki" : "Edit"}
                    </button>
                  </div>
                ) : (
                  <form style={{ marginTop: 24 }} className="col gap-3"
                    onSubmit={(e) => { e.preventDefault(); setJoinConfirm(true) }}>
                    <button type="button" onClick={backToIdentify}
                      style={{ fontSize: 12, color: "var(--text-3)", background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left" }}>
                      ← {lang === "id" ? "ganti" : "change"} · {lang === "id" ? "Kode" : "Code"}: <span className="mono">{joinCode}</span>
                    </button>
                    <div className="field">
                      <label className="field-label">{lang === "id" ? "Nama lengkap" : "Full name"}</label>
                      <input className="input" value={joinName} required minLength={2}
                        onChange={(e) => setJoinName(e.target.value)}
                        placeholder={lang === "id" ? "sesuai identitas" : "as on your ID"}
                        autoComplete="name" />
                    </div>
                    <div className="field">
                      <label className="field-label">{lang === "id" ? "Perusahaan" : "Company"}</label>
                      <input className="input" value={joinCompany}
                        onChange={(e) => setJoinCompany(e.target.value)}
                        placeholder={lang === "id" ? "tempat Anda bekerja" : "where you work"}
                        autoComplete="organization" />
                    </div>
                    <div className="field">
                      <label className="field-label">{lang === "id" ? "Tempat lahir" : "Place of birth"}</label>
                      <input className="input" value={joinPlaceOfBirth} required
                        onChange={(e) => setJoinPlaceOfBirth(e.target.value)}
                        placeholder={lang === "id" ? "kota kelahiran" : "city of birth"}
                        autoComplete="off" />
                    </div>
                    <div className="field">
                      <label className="field-label">{lang === "id" ? "Tanggal lahir" : "Date of birth"}</label>
                      <input className="input" type="date" value={joinDateOfBirth} required
                        onChange={(e) => setJoinDateOfBirth(e.target.value)}
                        autoComplete="bday" />
                    </div>
                    <div className="field">
                      <label className="field-label">{lang === "id" ? "Email (opsional)" : "Email (optional)"}</label>
                      <input className="input" type="email" value={joinEmail}
                        onChange={(e) => setJoinEmail(e.target.value)}
                        placeholder={lang === "id" ? "untuk reset password & notifikasi" : "for password reset & notifications"}
                        autoComplete="email" />
                    </div>
                    <div className="field">
                      <label className="field-label">{lang === "id" ? "No. telepon (opsional)" : "Phone number (optional)"}</label>
                      <input className="input" type="tel" value={joinPhone}
                        onChange={(e) => setJoinPhone(e.target.value)}
                        placeholder={lang === "id" ? "aktif WhatsApp" : "active WhatsApp"}
                        autoComplete="tel" />
                    </div>
                    <div className="field">
                      <label className="field-label">{lang === "id" ? "Kata sandi (opsional)" : "Password (optional)"}</label>
                      <input className="input" type="password" value={joinPassword} minLength={8}
                        onChange={(e) => setJoinPassword(e.target.value)}
                        placeholder={lang === "id" ? "agar bisa masuk lewat Email nanti" : "so you can sign in via Email later"}
                        autoComplete="new-password" />
                    </div>
                    <div style={{ fontSize: 12, color: "var(--text-3)", lineHeight: 1.5 }}>
                      {lang === "id"
                        ? "Hanya berlaku saat pendaftaran pertama kali; tidak mengubah kata sandi akun yang sudah ada."
                        : "Only applies the first time you join; won't change an existing account's password."}
                    </div>
                    <button className="btn-primary btn-lg btn-block" type="submit" style={{ marginTop: 4 }}>
                      {lang === "id" ? "Lanjut" : "Continue"} →
                    </button>
                    <div style={{ fontSize: 12, color: "var(--text-3)", lineHeight: 1.55, textAlign: "center" }}>
                      {lang === "id"
                        ? (joinEmail.trim()
                          ? "Sudah pernah mengisi sebelumnya? Isi email yang sama agar nilai Anda tidak terpisah."
                          : "Sudah pernah mengisi sebelumnya? Isi nama yang sama persis agar nilai Anda tidak terpisah.")
                        : (joinEmail.trim()
                          ? "Joined before? Enter the same email so your scores stay together."
                          : "Joined before? Enter exactly the same name so your scores stay together.")}
                    </div>
                  </form>
                )
              )}

              {/* ─ QR code ─ */}
              {step === "qr" && (
                <div style={{ marginTop: 24 }} className="col gap-3">
                  {!scanning && (
                    <button type="button" onClick={backToIdentify}
                      style={{ fontSize: 12, color: "var(--text-3)", background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left" }}>
                      ← {lang === "id" ? "ganti" : "change"}
                    </button>
                  )}
                  {!scanning ? (
                    <>
                      <div style={{
                        textAlign: "center",
                        padding: "28px 20px",
                        background: "var(--surface-2)",
                        border: "1px solid var(--border-subtle)",
                        borderRadius: "var(--r-lg)",
                      }}>
                        <div style={{
                          width: 96, height: 96, margin: "0 auto",
                          display: "grid", placeItems: "center",
                          background: "var(--surface)", border: "1px solid var(--border-subtle)",
                          borderRadius: "var(--r-md)",
                        }}>
                          <Icon name="qr" size={48} color="var(--program-color)" />
                        </div>
                        <h3 className="h3 mt-3">{lang === "id" ? "Scan QR dari email Anda" : "Scan QR from your email"}</h3>
                        <p className="small muted mt-2">
                          {lang === "id"
                            ? "Buka email enrollment, arahkan kamera ke QR Code, atau tempel token di bawah."
                            : "Open your enrollment email, point your camera at the QR, or paste the token below."}
                        </p>
                        <button className="btn-primary btn-lg btn-block" style={{ marginTop: 16 }}
                          onClick={() => {
                            if (!selectedCamId) { setCameraError(lang === "id" ? "Kamera belum siap." : "Camera not ready."); return }
                            setCameraError(null); setScanning(true)
                          }}
                          disabled={!selectedCamId}>
                          <Icon name="cam" size={14} /> {lang === "id" ? "Buka kamera" : "Open camera"}
                        </button>
                        {cameraError && <div className="tiny mt-2" style={{ color: "var(--danger)", textAlign: "left" }}>{cameraError}</div>}
                      </div>

                      <div className="field">
                        <label className="field-label">{lang === "id" ? "…atau tempel QR token manual" : "…or paste QR token manually"}</label>
                        <input className="input" type="text" value={qrTokenInput}
                          onChange={(e) => setQrTokenInput(e.target.value)} placeholder="eyJhbGc..." />
                      </div>
                      <button className="btn-primary btn-block" disabled={!qrTokenInput || loading}
                        onClick={() => handleQRLogin(qrTokenInput)}>
                        {loading ? <><Spinner /> {lang === "id" ? "Memproses…" : "Signing in…"}</> : (lang === "id" ? "Masuk dengan token" : "Sign in with token")}
                      </button>

                      {/* QR info */}
                      <div style={{
                        padding: 12, background: "var(--surface-2)",
                        border: "1px solid var(--border-subtle)", borderRadius: "var(--r-md)",
                        fontSize: 11, color: "var(--text-3)", display: "flex", gap: 8,
                      }}>
                        <Icon name="shield" size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                        <div>
                          {lang === "id"
                            ? "QR berputar setiap 60 detik · hanya valid dari perangkat terpercaya. Perangkat baru membutuhkan konfirmasi email."
                            : "QR rotates every 60s · only valid from a trusted device. New devices require email confirmation."}
                        </div>
                      </div>

                      {import.meta.env.DEV && <DemoCard lang={lang} loading={loading} onLogin={handleDemoLogin} />}
                    </>
                  ) : (
                    <div style={{ padding: 20, background: "#0B1417", borderRadius: "var(--r-lg)" }}>
                      {cameras.length > 1 && (
                        <div className="mb-2">
                          <label style={{ color: "var(--g-30)", fontSize: 11, display: "block", marginBottom: 4 }}>
                            {lang === "id" ? "Pilih Kamera" : "Select Camera"}
                          </label>
                          <select value={selectedCamId || ""}
                            onChange={(e) => setSelectedCamId(e.target.value)}
                            style={{ width: "100%", height: 32, padding: "0 8px", background: "#101E23", color: "white", border: "1px solid #3C4549", borderRadius: 4 }}>
                            {cameras.map((c) => (
                              <option key={c.id} value={c.id}>{c.label || `Camera ${c.id.slice(0, 6)}`}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div id="qr-reader-login" style={{
                        width: "100%", aspectRatio: "1 / 1",
                        border: "2px solid rgba(255,255,255,.25)",
                        background: "#0a1018", overflow: "hidden", borderRadius: 8,
                      }} />
                      <div style={{ color: "var(--g-30)", textAlign: "center", fontSize: 11, fontFamily: "var(--font-mono)", letterSpacing: ".08em", textTransform: "uppercase", marginTop: 12 }}>
                        {lang === "id" ? "Mendeteksi QR…" : "Scanning for QR…"}
                      </div>
                      {cameraError && (
                        <div className="tiny mt-2" style={{ color: "#ff8a80", textAlign: "center" }}>{cameraError}</div>
                      )}
                      <button className="btn-secondary btn-block" style={{ marginTop: 12 }} onClick={() => setScanning(false)}>
                        {t.cancel || "Cancel"}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Divider + new user hint — hanya di Langkah 1, bukan di
                  setiap sub-form */}
              {step === "identify" && (
                <>
                  <div className="divider" style={{ margin: "28px 0 20px" }} />
                  <div style={{ textAlign: "center", fontSize: 12, color: "var(--text-3)" }}>
                    {lang === "id"
                      ? `Baru di ${orgName}? Minta admin program untuk undangan enrollment.`
                      : `New to ${orgName}? Ask your program admin for an enrollment invite.`}
                    {" "}<a href="#" onClick={(e) => { e.preventDefault(); navigate("claim") }}
                      style={{ color: "var(--program-color-text)", fontWeight: 500 }}>
                      {lang === "id" ? "Klaim sertifikat" : "Claim certificate"}
                    </a>
                  </div>
                </>
              )}
            </>
          )}
    </LoginShell>
  )
}

/* ── Demo login card (reused in password + QR modes) ───────── */
function DemoCard({ lang, loading, onLogin }) {
  return (
    <div style={{
      padding: "12px 14px",
      background: "var(--surface-2)",
      border: "1px solid var(--border-subtle)",
      borderRadius: "var(--r-md)",
    }}>
      <div className="eyebrow mb-2">{lang === "id" ? "Demo · masuk sebagai" : "Demo · sign in as"}</div>
      <div className="row gap-2">
        {[
          { r: "participant", label: lang === "id" ? "Peserta" : "Participant" },
          { r: "trainer",     label: "Trainer" },
          { r: "admin",       label: "Admin" },
        ].map((x) => (
          <button key={x.r} className="btn-secondary btn-sm"
            style={{ flex: 1, justifyContent: "center", padding: "0 8px", minWidth: 0 }}
            disabled={loading} type="button"
            onClick={() => onLogin(x.r)}>
            {x.label}
          </button>
        ))}
      </div>
    </div>
  )
}

export default LoginScreen
